/* ═══════════════════════════════════════════════════════════════════════════
 * RSV Quiz Tracking Module — v2.0 (2026-04-22)
 * ═══════════════════════════════════════════════════════════════════════════
 * Single source of truth for all quiz tracking fires.
 *
 * Architecture:
 *   Server-only-via-Elevar for all ad-platform events. This module does NOT
 *   call fbq() or gtag() directly. Instead it pushes dl_* events to both
 *   window.dataLayer AND window.ElevarDataLayer. Elevar's Shopify Web Pixel
 *   fans out to Meta (browser + CAPI), Google Ads, and GA4 using the
 *   event_id on each push as the cross-source dedup key.
 *
 *   Klaviyo, Google Sheets, and PostHog are fired directly (not via Elevar).
 *
 * Public API:
 *   window.RSV_QUIZ_TRACK.fire(event_name, data)      → returns event_id
 *   window.RSV_QUIZ_TRACK.fireConversion(reason, data) → returns event_id
 *   window.RSV_QUIZ_TRACK.setUser({ email, phone, first_name, last_name })
 *   window.RSV_QUIZ_TRACK.setQuiz({ quiz_id, quiz_variant, total_steps })
 *   window.RSV_QUIZ_TRACK.getMeta() → { utm_*, fbp, fbc, gclid, session_id, ... }
 *
 * Config is read from window.RSV_QUIZ_TRACK_CONFIG, injected by
 * snippets/rsv-quiz-tracking.liquid from section settings.
 *
 * Internal event_name → (dl event name, Klaviyo metric name) mapping lives in
 * the EVENT_MAP constant below. The dl event name must be registered in the
 * Elevar admin UI with a destination mapping before the event will forward.
 * ═══════════════════════════════════════════════════════════════════════════ */

(function() {
  'use strict';

  if (window.RSV_QUIZ_TRACK) return; // idempotent load guard

  var CFG = window.RSV_QUIZ_TRACK_CONFIG || {};
  var LOG = CFG.debug ? function(){ console.log.apply(console, ['[RSV_TRACK]'].concat([].slice.call(arguments))); } : function(){};
  var WARN = function(){ console.warn.apply(console, ['[RSV_TRACK]'].concat([].slice.call(arguments))); };

  /* ── Event mapping ───────────────────────────────────────────────────────
   * Internal name ↔ Elevar dl event ↔ Klaviyo metric.
   * For any event here whose dl name starts with `dl_` but is NOT a Shopify
   * standard (dl_subscribe, dl_purchase, dl_view_item, etc.), it MUST be
   * registered in Elevar admin UI before forwarding to Meta/Google/GA4 works.
   */
  /* Elevar standard events (auto-forward to destinations):
   *   dl_subscribe       → Meta Email Sign Up, Google Ads conversion, GA4 generate_lead
   *   dl_complete_quiz   → Meta Complete Quiz (mid-funnel standard event, verified 2026-04-22)
   *   dl_user_data       → identity stitch (no destination fire, just enriches session)
   *
   * Non-standard dl_* events (won't auto-forward until Elevar Custom Events launches):
   *   dl_quiz_started, dl_quiz_step_viewed, dl_quiz_step_completed, etc.
   *   These still push to dataLayer so PostHog bridge + GA4 can consume them.
   */
  var EVENT_MAP = {
    quiz_started:                { dl: 'dl_quiz_started',                klaviyo: 'Quiz Started' },
    quiz_step_viewed:            { dl: 'dl_quiz_step_viewed',            klaviyo: 'Quiz Step Viewed' },
    quiz_step_completed:         { dl: 'dl_quiz_step_completed',         klaviyo: 'Quiz Step Completed' },
    quiz_branched:               { dl: 'dl_quiz_branched',               klaviyo: 'Quiz Branched' },
    quiz_product_impression:     { dl: 'dl_quiz_product_impression',     klaviyo: 'Quiz Product Impression' },
    quiz_product_selected:       { dl: 'dl_quiz_product_selected',       klaviyo: 'Quiz Product Selected' },
    quiz_qualifier_conversion:   { dl: 'dl_quiz_qualifier_conversion',   klaviyo: 'Quiz Qualifier Converted' },
    quiz_lead:                   { dl: 'dl_subscribe',                   klaviyo: 'Quiz Completed' },
    quiz_completed:              { dl: 'dl_complete_quiz',               klaviyo: 'Quiz Completed Extended' },
    quiz_user_data:              { dl: 'dl_user_data',                   klaviyo: null }
  };

  /* ── State ─────────────────────────────────────────────────────────────── */
  var state = {
    user: {},                  // email, phone, first_name, last_name
    quiz: {                    // id, variant, total_steps
      quiz_id: CFG.quiz_id || 'stone-quiz',
      quiz_variant: readVariant(),
      total_steps: null
    },
    meta: buildMeta(),         // UTMs, fbp/fbc/gclid, session_id, rsv_lead_id
    fired: new Set()           // dedup keys to suppress re-fires in same session
  };

  /* ── UUID generation ───────────────────────────────────────────────────── */
  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    // RFC4122 v4 fallback
    var hex = '0123456789abcdef';
    var s = '';
    for (var i = 0; i < 36; i++) {
      if (i === 8 || i === 13 || i === 18 || i === 23) s += '-';
      else if (i === 14) s += '4';
      else if (i === 19) s += hex[(Math.random() * 4) | 0 | 8];
      else s += hex[(Math.random() * 16) | 0];
    }
    return s;
  }

  /* ── Cookie + storage helpers ──────────────────────────────────────────── */
  function getCookie(name) {
    var m = document.cookie.match('(?:^|; )' + encodeURIComponent(name).replace(/[.*+?^${}()|[\]\\]/g,'\\$&') + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : null;
  }

  function setCookie(name, value, days) {
    try {
      var d = new Date();
      d.setTime(d.getTime() + (days * 86400000));
      document.cookie = name + '=' + encodeURIComponent(value) + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
    } catch (e) {}
  }

  function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function ssGet(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} }

  /* ── Variant assignment (from PostHog feature flag) ─────────────────────
   * Reads in this priority order:
   *   1. URL param ?quiz_variant=xyz (for manual override during testing)
   *   2. localStorage rsv_quiz_variant (set when PostHog flag resolves)
   *   3. 'control'
   */
  function readVariant() {
    try {
      var url = new URL(window.location.href);
      var urlVar = url.searchParams.get('quiz_variant');
      if (urlVar) {
        lsSet('rsv_quiz_variant', urlVar);
        return urlVar;
      }
    } catch (e) {}
    return lsGet('rsv_quiz_variant') || 'control';
  }

  /* Called externally (by variant-loader snippet) when PostHog flag resolves. */
  function setVariant(v) {
    if (!v) return;
    state.quiz.quiz_variant = v;
    lsSet('rsv_quiz_variant', v);
    try { document.body.setAttribute('data-quiz-variant', v); } catch (e) {}
  }

  /* ── Meta (URL, session) capture ───────────────────────────────────────── */
  function buildMeta() {
    var u;
    try { u = new URL(window.location.href); } catch (e) { u = null; }
    var q = u ? u.searchParams : null;
    var g = function(k) { return q ? q.get(k) : null; };

    // UTMs — URL wins on current load, fall back to persisted values
    // (first-touch attribution: only write once per session)
    var persisted = {};
    try { persisted = JSON.parse(ssGet('rsv_quiz_attr') || '{}'); } catch (e) {}

    var utm_source   = g('utm_source')   || persisted.utm_source   || null;
    var utm_medium   = g('utm_medium')   || persisted.utm_medium   || null;
    var utm_campaign = g('utm_campaign') || persisted.utm_campaign || null;
    var utm_content  = g('utm_content')  || persisted.utm_content  || null;
    var utm_term     = g('utm_term')     || persisted.utm_term     || null;
    var utm_id       = g('utm_id')       || persisted.utm_id       || null;

    // If URL has new UTMs, overwrite session store (last-click within session)
    if (g('utm_source') || g('utm_campaign')) {
      ssSet('rsv_quiz_attr', JSON.stringify({
        utm_source: utm_source, utm_medium: utm_medium, utm_campaign: utm_campaign,
        utm_content: utm_content, utm_term: utm_term, utm_id: utm_id
      }));
    }

    // Facebook click ID (fbclid) → transform to fbc format if Elevar isn't setting it
    var fbp = getCookie('_fbp');
    var fbc = getCookie('_fbc');
    var fbclid = g('fbclid');
    if (!fbc && fbclid) {
      fbc = 'fb.1.' + Date.now() + '.' + fbclid;
      setCookie('_fbc', fbc, 90);
    }

    // Google click ID
    var gclid = g('gclid') || lsGet('rsv_gclid');
    if (g('gclid')) lsSet('rsv_gclid', gclid);

    // Stable session id across page loads in same session
    var session_id = ssGet('rsv_quiz_session_id');
    if (!session_id) {
      session_id = uuid();
      ssSet('rsv_quiz_session_id', session_id);
    }

    // Stable per-browser lead id (for post-quiz cross-session identity)
    var rsv_lead_id = getCookie('rsv_lead_id') || lsGet('rsv_lead_id');
    if (!rsv_lead_id) {
      rsv_lead_id = uuid();
      setCookie('rsv_lead_id', rsv_lead_id, 365);
      lsSet('rsv_lead_id', rsv_lead_id);
    }

    return {
      utm_source: utm_source, utm_medium: utm_medium, utm_campaign: utm_campaign,
      utm_content: utm_content, utm_term: utm_term, utm_id: utm_id,
      fbp: fbp, fbc: fbc, gclid: gclid,
      landing_page: window.location.pathname,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      session_id: session_id,
      rsv_lead_id: rsv_lead_id
    };
  }

  /* ── Event object construction ─────────────────────────────────────────── */
  function buildEvent(eventName, data) {
    data = data || {};
    var mapping = EVENT_MAP[eventName] || { dl: 'dl_' + eventName, klaviyo: eventName };

    var evt = {
      event_id: data.event_id || uuid(),
      event_name: eventName,
      dl_event: mapping.dl,
      klaviyo_metric: mapping.klaviyo,
      event_time: Math.floor(Date.now() / 1000),
      event_source_url: window.location.href,
      action_source: 'website',

      // User
      email: data.email || state.user.email || null,
      phone: data.phone || state.user.phone || null,
      first_name: data.first_name || state.user.first_name || null,
      last_name: data.last_name || state.user.last_name || null,
      external_id: state.meta.rsv_lead_id,

      // Quiz
      quiz_id: data.quiz_id || state.quiz.quiz_id,
      quiz_variant: data.quiz_variant || state.quiz.quiz_variant,
      total_steps: data.total_steps || state.quiz.total_steps,
      step_num: data.step_num || null,
      step_handle: data.step_handle || null,
      step_answer: data.step_answer || null,
      selected_products: data.selected_products || null,
      answers: data.answers || null,
      raw: data._raw || null,
      total_time_seconds: data.total_time_seconds || null,

      // Attribution
      utm_source: data.utm_source || state.meta.utm_source,
      utm_medium: data.utm_medium || state.meta.utm_medium,
      utm_campaign: data.utm_campaign || state.meta.utm_campaign,
      utm_content: data.utm_content || state.meta.utm_content,
      utm_term: data.utm_term || state.meta.utm_term,
      utm_id: data.utm_id || state.meta.utm_id,
      fbp: state.meta.fbp,
      fbc: state.meta.fbc,
      gclid: state.meta.gclid,
      landing_page: state.meta.landing_page,
      referrer: state.meta.referrer,

      // Value
      value: data.value != null ? data.value : (CFG.lead_value || 25),
      currency: data.currency || 'USD',

      // A/B & trigger context
      trigger_reason: data.trigger_reason || null,
      conversion_trigger_spec: data.conversion_trigger_spec || null
    };

    // Dedup key: one fire per (event_name, step_handle) per session
    evt.dedupe_key = eventName + '|' + (evt.step_handle || '') + '|' + state.meta.session_id;
    return evt;
  }

  /* ═══════════════════════════════════════════════════════════════════════
   * Channel: Elevar dataLayer
   * Pushes to BOTH window.dataLayer (standard) and window.ElevarDataLayer
   * (Elevar's dedicated layer). Defensive — some Elevar versions read one,
   * some read the other, and pushing to both has no downside.
   * ═══════════════════════════════════════════════════════════════════════ */
  function elevarSend(evt) {
    var payload = {
      event: evt.dl_event,
      event_id: evt.event_id,
      event_time: evt.event_time,
      event_source_url: evt.event_source_url,
      action_source: evt.action_source,

      // dl_subscribe standard fields
      lead_type: evt.email ? 'email' : (evt.phone ? 'phone' : undefined),

      // user_properties — Elevar standard shape
      user_properties: {
        customer_email: evt.email,
        customer_phone: evt.phone,
        first_name: evt.first_name,
        last_name: evt.last_name,
        external_id: evt.external_id,
        visitor_type: evt.email ? 'lead' : 'guest'
      },

      // marketing block — Elevar propagates these to GA4 / Meta as event_params
      marketing: {
        utm_source: evt.utm_source,
        utm_medium: evt.utm_medium,
        utm_campaign: evt.utm_campaign,
        utm_content: evt.utm_content,
        utm_term: evt.utm_term,
        utm_id: evt.utm_id,
        fbp: evt.fbp,
        fbc: evt.fbc,
        gclid: evt.gclid,
        landing_page: evt.landing_page,
        referrer: evt.referrer
      },

      // quiz-specific (custom properties — available in destinations as event params)
      quiz: {
        quiz_id: evt.quiz_id,
        quiz_variant: evt.quiz_variant,
        step_num: evt.step_num,
        step_handle: evt.step_handle,
        step_answer: evt.step_answer,
        total_steps: evt.total_steps,
        answers: evt.answers,
        selected_products: evt.selected_products,
        trigger_reason: evt.trigger_reason,
        conversion_trigger_spec: evt.conversion_trigger_spec
      },

      // conversion value
      value: evt.value,
      currency: evt.currency
    };

    // Prune undefined/null top-level fields to keep payload clean
    for (var k in payload) {
      if (payload[k] === undefined || payload[k] === null) delete payload[k];
    }

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) { WARN('dataLayer push failed', e); }

    try {
      window.ElevarDataLayer = window.ElevarDataLayer || [];
      window.ElevarDataLayer.push(payload);
    } catch (e) { WARN('ElevarDataLayer push failed', e); }

    LOG('Elevar push', evt.dl_event, evt.event_id);
  }

  /* ═══════════════════════════════════════════════════════════════════════
   * Channel: Klaviyo (direct client API)
   * ═══════════════════════════════════════════════════════════════════════ */
  function klaviyoSend(evt) {
    if (!CFG.klaviyo_key) return;
    if (!evt.klaviyo_metric) return; // some events skip Klaviyo
    if (!evt.email && !evt.phone) return; // Klaviyo requires at least one identifier

    var profileAttrs = {
      email: evt.email,
      phone_number: evt.phone,
      first_name: evt.first_name,
      last_name: evt.last_name,
      properties: {
        rsv_lead_id: evt.external_id,
        latest_quiz_variant: evt.quiz_variant,
        latest_quiz_step: evt.step_handle,
        utm_source: evt.utm_source,
        utm_medium: evt.utm_medium,
        utm_campaign: evt.utm_campaign,
        utm_content: evt.utm_content
      }
    };

    var eventAttrs = {
      properties: {
        event_id: evt.event_id,
        internal_event_name: evt.event_name,
        quiz_id: evt.quiz_id,
        quiz_variant: evt.quiz_variant,
        step_num: evt.step_num,
        step_handle: evt.step_handle,
        step_answer: evt.step_answer,
        total_steps: evt.total_steps,
        selected_products: evt.selected_products,
        answers: evt.answers,
        utm_source: evt.utm_source,
        utm_medium: evt.utm_medium,
        utm_campaign: evt.utm_campaign,
        utm_content: evt.utm_content,
        utm_term: evt.utm_term,
        trigger_reason: evt.trigger_reason,
        conversion_trigger_spec: evt.conversion_trigger_spec,
        rsv_lead_id: evt.external_id
      },
      metric: { data: { type: 'metric', attributes: { name: evt.klaviyo_metric } } },
      profile: { data: { type: 'profile', attributes: profileAttrs } },
      unique_id: evt.event_id,
      value: evt.value,
      value_currency: evt.currency,
      time: new Date(evt.event_time * 1000).toISOString()
    };

    var body = { data: { type: 'event', attributes: eventAttrs } };
    var url = 'https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(CFG.klaviyo_key);
    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify(body),
        keepalive: true
      }).catch(function(e) { WARN('Klaviyo event POST failed', e); });

      // Also upsert profile (so profile properties like rsv_lead_id + utm get stored even if metric fails)
      var pUrl = 'https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(CFG.klaviyo_key);
      fetch(pUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify({ data: { type: 'profile', attributes: profileAttrs } }),
        keepalive: true
      }).catch(function(e) { WARN('Klaviyo profile POST failed', e); });

      LOG('Klaviyo sent', evt.klaviyo_metric, evt.event_id);
    } catch (e) { WARN('Klaviyo send error', e); }
  }

  /* ═══════════════════════════════════════════════════════════════════════
   * Channel: Google Sheets webhook (raw log)
   * ═══════════════════════════════════════════════════════════════════════ */
  function sheetsSend(evt) {
    if (!CFG.sheets_webhook) return;
    // The Google Sheet is the lead pipeline. Send one row on the real lead
    // event only; completion/qualifier events remain for analytics platforms.
    if (evt.event_name !== 'quiz_lead') return;

    function listValues(values) {
      if (!Array.isArray(values)) return values || '';
      return values.map(function(v) {
        if (!v) return '';
        if (typeof v === 'string') return v;
        return v.title || v.handle || v.id || '';
      }).filter(Boolean).join(', ');
    }

    function productHandles(products) {
      if (!Array.isArray(products)) return '';
      return products.map(function(p) {
        if (!p) return '';
        if (typeof p === 'string') return p;
        return p.handle || p.title || p.id || '';
      }).filter(Boolean).join(', ');
    }

    // The Apps Script webhook still expects the legacy flat quizPayload shape
    // from stone-quiz.liquid. Prefer that exact payload when the section passes
    // it, and keep a fallback for any future lightweight caller.
    var raw = evt.raw && typeof evt.raw === 'object' ? evt.raw : null;
    var row = raw ? Object.assign({}, raw) : {
      timestamp: new Date(evt.event_time * 1000).toISOString(),
      name: [evt.first_name, evt.last_name].filter(Boolean).join(' '),
      email: evt.email,
      phone: evt.phone,
      styles: evt.answers && evt.answers.styles ? listValues(evt.answers.styles) : '',
      colors: evt.answers && evt.answers.colors ? listValues(evt.answers.colors) : '',
      samples: evt.answers && evt.answers.samples ? listValues(evt.answers.samples) : listValues(evt.selected_products),
      sample_handles: productHandles(evt.selected_products),
      qualifier_answers: evt.answers && evt.answers.qualifiers ? evt.answers.qualifiers : {},
      consent_transactional: false,
      consent_marketing: false,
      total_time_seconds: evt.total_time_seconds || 0,
      step_timings: '',
      products_shown: Array.isArray(evt.selected_products) ? evt.selected_products.length : 0,
      device: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
      utm_source: evt.utm_source,
      utm_medium: evt.utm_medium,
      utm_campaign: evt.utm_campaign,
      utm_content: evt.utm_content,
      fbclid: evt.fbc,
      gclid: evt.gclid,
      referrer: evt.referrer
    };

    row.event_id = evt.event_id;
    row.event_name = evt.event_name;
    row.quiz_id = evt.quiz_id;
    row.quiz_variant = evt.quiz_variant;
    row.rsv_lead_id = evt.external_id;

    var json = JSON.stringify(row);
    try {
      if (navigator.sendBeacon) {
        var blob = new Blob([json], { type: 'text/plain;charset=UTF-8' });
        if (navigator.sendBeacon(CFG.sheets_webhook, blob)) {
          LOG('Sheets sent (beacon)', evt.event_name);
          return;
        }
      }
      // Fallback
      fetch(CFG.sheets_webhook, {
        method: 'POST',
        body: json,
        keepalive: true,
        mode: 'no-cors'
      });
      LOG('Sheets sent (fetch)', evt.event_name);
    } catch (e) { WARN('Sheets send failed', e); }
  }

  /* ═══════════════════════════════════════════════════════════════════════
   * Channel: PostHog (direct capture)
   * Note: the existing theme.liquid PostHog↔dataLayer bridge will also
   * capture dl_* pushes. We want PostHog to see events by their internal
   * name (quiz_lead, quiz_step_completed, ...) for clean dashboards, NOT
   * the dl_* names. So we fire directly here and skip the bridge for our
   * events by using internal names.
   * ═══════════════════════════════════════════════════════════════════════ */
  function postHogSend(evt) {
    if (!window.posthog || !window.posthog.capture) return;

    try {
      // Use the pseudonymous RSV lead ID for behavior joins. Contact data stays
      // in consented lead systems (Klaviyo/Sheets), never in PostHog events.
      if (evt.external_id && window.posthog.get_distinct_id && window.posthog.get_distinct_id() !== evt.external_id) {
        window.posthog.identify(evt.external_id, {
          rsv_lead_id: evt.external_id,
          lead_state: 'known'
        });
      }

      // Always register quiz variant as a super property
      if (window.posthog.register && evt.quiz_variant) {
        window.posthog.register({
          quiz_variant: evt.quiz_variant,
          quiz_id: evt.quiz_id
        });
      }

      window.posthog.capture(evt.event_name, {
        event_id: evt.event_id,
        quiz_id: evt.quiz_id,
        quiz_variant: evt.quiz_variant,
        step_num: evt.step_num,
        step_handle: evt.step_handle,
        total_steps: evt.total_steps,
        selected_product_ids: Array.isArray(evt.selected_products) ? evt.selected_products.map(function (product) {
          return product && (product.id || product.product_id || product.variant_id);
        }).filter(Boolean).slice(0, 12) : null,
        utm_source: evt.utm_source,
        utm_medium: evt.utm_medium,
        utm_campaign: evt.utm_campaign,
        utm_content: evt.utm_content,
        utm_term: evt.utm_term,
        utm_id: evt.utm_id,
        rsv_lead_id: evt.external_id,
        value: evt.value,
        currency: evt.currency,
        trigger_reason: evt.trigger_reason,
        conversion_trigger_spec: evt.conversion_trigger_spec
      });
      LOG('PostHog sent', evt.event_name, evt.event_id);
    } catch (e) { WARN('PostHog send failed', e); }
  }

  /* ═══════════════════════════════════════════════════════════════════════
   * Channel: Shopify cart attributes (for identity stitching post-checkout)
   * Attaches rsv_lead_id to the cart so when the order webhook fires on
   * Shopify side, Elevar can match back to the original quiz lead.
   * ═══════════════════════════════════════════════════════════════════════ */
  function cartAttributeSend(evt) {
    if (evt.event_name !== 'quiz_lead') return; // only on lead completion
    if (!evt.external_id) return;

    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          attributes: {
            rsv_lead_id: evt.external_id,
            rsv_quiz_variant: evt.quiz_variant,
            rsv_utm_source: evt.utm_source,
            rsv_utm_campaign: evt.utm_campaign,
            rsv_utm_content: evt.utm_content,
            rsv_quiz_email: evt.email
          }
        }),
        keepalive: true
      }).catch(function(e) { WARN('Cart attribute update failed', e); });
      LOG('Cart attributes set', evt.external_id);
    } catch (e) { WARN('Cart attribute error', e); }
  }

  /* ═══════════════════════════════════════════════════════════════════════
   * Fire dispatcher
   * ═══════════════════════════════════════════════════════════════════════ */
  function fire(eventName, data) {
    try {
      if (!CFG.enabled && CFG.enabled !== undefined) {
        LOG('Fire skipped (tracking disabled)', eventName);
        return null;
      }
      var evt = buildEvent(eventName, data);

      if (state.fired.has(evt.dedupe_key)) {
        LOG('Fire skipped (dedup)', eventName, evt.dedupe_key);
        return evt.event_id;
      }
      state.fired.add(evt.dedupe_key);

      elevarSend(evt);
      klaviyoSend(evt);
      sheetsSend(evt);
      postHogSend(evt);
      cartAttributeSend(evt);

      return evt.event_id;
    } catch (e) {
      WARN('fire() failed', eventName, e);
      return null;
    }
  }

  function fireConversion(triggerReason, data) {
    data = data || {};
    data.trigger_reason = triggerReason;
    data.conversion_trigger_spec = CFG.conversion_trigger_spec || null;
    // Conversion uses the quiz_qualifier_conversion event name (mid-funnel)
    // unless caller overrides via data.conversion_event_name
    var eventName = data.conversion_event_name || 'quiz_qualifier_conversion';
    return fire(eventName, data);
  }

  /* ═══════════════════════════════════════════════════════════════════════
   * Public setters
   * ═══════════════════════════════════════════════════════════════════════ */
  function setUser(u) {
    if (!u) return;
    if (u.email) state.user.email = String(u.email).trim().toLowerCase();
    if (u.phone) state.user.phone = String(u.phone).trim();
    if (u.first_name) state.user.first_name = String(u.first_name).trim();
    if (u.last_name) state.user.last_name = String(u.last_name).trim();
    // Fire a dl_user_data push immediately so Elevar stitches identity across events
    if (state.user.email || state.user.phone) {
      fire('quiz_user_data', {});
    }
  }

  function setQuiz(q) {
    if (!q) return;
    if (q.quiz_id) state.quiz.quiz_id = q.quiz_id;
    if (q.quiz_variant) setVariant(q.quiz_variant);
    if (q.total_steps) state.quiz.total_steps = q.total_steps;
  }

  function getMeta() {
    return Object.assign({}, state.meta, { quiz_variant: state.quiz.quiz_variant });
  }

  /* ═══════════════════════════════════════════════════════════════════════
   * Export
   * ═══════════════════════════════════════════════════════════════════════ */
  window.RSV_QUIZ_TRACK = {
    fire: fire,
    fireConversion: fireConversion,
    setUser: setUser,
    setQuiz: setQuiz,
    setVariant: setVariant,
    getMeta: getMeta,
    _state: state, /* for debugging only */
    _cfg: CFG
  };

  LOG('RSV_QUIZ_TRACK ready', 'variant=' + state.quiz.quiz_variant, 'session_id=' + state.meta.session_id, 'rsv_lead_id=' + state.meta.rsv_lead_id);

  // Emit a ready event so section code can wait on it
  try {
    window.dispatchEvent(new CustomEvent('rsv-quiz-track-ready', { detail: { variant: state.quiz.quiz_variant } }));
  } catch (e) {}
})();
